import { useMemo } from 'react';
import type { User } from '../stores/slices/authSlice';
import type { PermissionCheckOptions, PermissionCheckResult } from '../types/permission';

/**
 * 检查用户是否拥有指定权限
 * 
 * @param user - 用户信息
 * @param permissions - 需要的权限列表（AND 关系）
 * @returns 是否拥有所有权限
 */
export function checkPermissions(user: User | null, permissions: string[]): boolean {
  if (!user || !permissions || permissions.length === 0) {
    return true; // 没有权限要求，默认通过
  }
  
  const userPermissions = user.permissions || [];
  
  // AND 关系：需要拥有所有权限
  return permissions.every((permission) => userPermissions.includes(permission));
}

/**
 * 检查用户是否拥有指定角色
 * 
 * @param user - 用户信息
 * @param roles - 需要的角色列表（OR 关系）
 * @returns 是否拥有任一角色
 */
export function checkRoles(user: User | null, roles: string[]): boolean {
  if (!user || !roles || roles.length === 0) {
    return true; // 没有角色要求，默认通过
  }
  
  const userRoles = user.roles || [];
  
  // OR 关系：拥有任一角色即可
  return roles.some((role) => userRoles.includes(role));
}

/**
 * 综合权限检查
 * 
 * @param user - 用户信息
 * @param options - 权限检查选项
 * @returns 权限检查结果
 */
export function checkPermission(
  user: User | null,
  options: PermissionCheckOptions
): PermissionCheckResult {
  const { permissions, roles, modules, customCheck, mode = 'all' } = options;
  
  // 如果有自定义检查函数，优先使用
  if (customCheck) {
    const hasPermission = customCheck(user);
    return { hasPermission };
  }
  
  // 检查各项权限
  const hasPermissions = checkPermissions(user, permissions || []);
  const hasRoles = checkRoles(user, roles || []);
  const hasModules = !modules || modules.length === 0
    ? true
    : modules.some((module) => (user?.permissions || []).some((perm) => perm.startsWith(`${module}:`)));
  
  // 根据模式判断
  let hasPermission: boolean;
  if (mode === 'any') {
    // any 模式：满足任一条件即可
    hasPermission = hasPermissions || hasRoles || hasModules;
  } else {
    // all 模式：所有条件都满足
    hasPermission = hasPermissions && hasRoles && hasModules;
  }
  
  // 收集缺少的权限信息
  const result: PermissionCheckResult = { hasPermission };
  
  if (!hasPermission) {
    if (!hasPermissions && permissions && permissions.length > 0) {
      const userPermissions = user?.permissions || [];
      result.missingPermissions = permissions.filter((p) => !userPermissions.includes(p));
    }
    if (!hasRoles && roles && roles.length > 0) {
      const userRoles = user?.roles || [];
      result.missingRoles = roles.filter((r) => !userRoles.includes(r));
    }
  }
  
  return result;
}

/**
 * 权限检查 Hook
 * 
 * 用于在组件中判断用户是否有权限
 * 
 * @param user - 用户信息
 * @param options - 权限检查选项
 * @returns 权限检查结果
 * 
 * @example
 * ```tsx
 * // 基本用法
 * const user = useAppSelector((state) => state.auth.user);
 * const { hasPermission } = usePermission(user, {
 *   permissions: ['user:delete'],
 * });
 * 
 * // 性能优化：如果 options 在组件中动态创建，使用 useMemo 稳定引用
 * const options = useMemo(() => ({
 *   permissions: ['user:delete'],
 *   mode: 'all',
 * }), []);
 * const { hasPermission } = usePermission(user, options);
 * 
 * return (
 *   <Button disabled={!hasPermission}>删除</Button>
 * );
 * ```
 */
export function usePermission(
  user: User | null,
  options: PermissionCheckOptions
): PermissionCheckResult {
  // 解构 options 以确保依赖项稳定
  // 注意：如果调用者每次都创建新的 options 对象，建议使用 useMemo 稳定 options
  const { permissions, roles, modules, customCheck, mode } = options;
  
  return useMemo(
    () => checkPermission(user, options),
    // 依赖具体的字段而不是整个 options 对象，避免不必要的重新计算
    // 注意：permissions 和 roles 数组引用变化时会重新计算（这是期望的行为）
    [user, permissions, roles, modules, customCheck, mode]
  );
}

/**
 * 简化版：只检查权限列表
 * 
 * @param user - 用户信息
 * @param permissions - 需要的权限列表
 * @returns 是否拥有权限
 */
export function useHasPermissions(user: User | null, permissions: string[]): boolean {
  return useMemo(() => checkPermissions(user, permissions), [user, permissions]);
}

/**
 * 简化版：只检查角色列表
 * 
 * @param user - 用户信息
 * @param roles - 需要的角色列表
 * @returns 是否拥有角色
 */
export function useHasRoles(user: User | null, roles: string[]): boolean {
  return useMemo(() => checkRoles(user, roles), [user, roles]);
}

const PLATFORM_TYPE = 'PLATFORM';

/**
 * 检查用户是否为平台所有者
 * 依据：auth 信息中 spaces[0].type === "PLATFORM" 且 spaces[0].ownerId === 当前用户 id
 *
 * @param user - 用户信息
 * @returns 是否为平台所有者
 */
export function isPlatformOwner(user: User | null): boolean {
  if (!user?.id || !user.spaces?.length) {
    return false;
  }
  const first = user.spaces[0];
  return first.type === PLATFORM_TYPE && first.ownerId === user.id;
}

/**
 * 是否平台所有者 Hook
 *
 * @param user - 用户信息
 * @returns 是否为平台所有者
 */
export function useIsPlatformOwner(user: User | null): boolean {
  return useMemo(() => isPlatformOwner(user), [user]);
}


